var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';
import styled from 'styled-components';
import DefaultNewMessageIndicator from './DefaultNewMessagesIndicator';
import MessageStore from './MessageStore';
import NoMessages from './NoMessages';
import { OverlayDiv } from './OverlayDiv';
const VirtuosoWrapper = styled.div `
  scrollbar-color: black transparent;
  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: black;
    border-radius: 6px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: black;
  }
  * {
    scrollbar-color: black transparent;
  }
  html::-webkit-scrollbar {
    width: 12px;
  }
  html::-webkit-scrollbar-thumb {
    background-color: black;
    border-radius: 6px;
  }
  html::-webkit-scrollbar-thumb:hover {
    background-color: black;
  }
`;
const VirtualizedChat = ({ loadPrevMessages, loadInitialMessages, incomingMessages = [], updatedMessages = [], render, newMessageIndicator = DefaultNewMessageIndicator, onItemNewItemRender, shouldTriggerNewItemIndicator, style, chatId, }) => {
    const [messages, setMessages] = useState([]);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    const [isLoadingInitial, setIsLoadingInitial] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [firstItemIndex, setFirstItemIndex] = useState(10000);
    const [totalCount, setTotalCount] = useState(10000);
    const listHandler = useRef(null);
    const [hasNewMessages, setHasNewMessages] = useState(false);
    const [oldestMessagerReported, setOldestMessageReported] = useState(-1);
    const store = useRef(new MessageStore()).current;
    const isScrolling = useRef(false);
    const isAtBottom = useRef(true);
    const isInitialLoadingRef = useRef(false);
    const scrollToBottom = () => {
        var _a;
        (_a = listHandler.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex({ index: 'LAST' });
    };
    const fetchInitialMessages = () => __awaiter(void 0, void 0, void 0, function* () {
        // TODO:  This is a hack to prevent multiple initial fetches between re-renders
        // caused by the component being unmounted and remounted during the
        // initial fetch. Need to find a better way to do this.
        isInitialLoadingRef.current = true;
        setIsLoadingInitial(true);
        setOldestMessageReported(-1);
        try {
            const initialMessagesResponse = yield loadInitialMessages();
            const { messages: initialMessages, totalCount: totalItems } = initialMessagesResponse || {};
            store.initial(initialMessages);
            setMessages(store.messages);
            setHasMore(totalItems > initialMessages.length);
            setFirstItemIndex(totalItems - initialMessages.length);
            setTotalCount(totalItems);
            // Force a re-render after a short delay to ensure proper rendering
            setTimeout(() => {
                setMessages([...store.messages]);
                scrollToBottom();
            }, 100);
        }
        catch (error) {
            console.error('Failed to load initial messages:', error);
        }
        finally {
            setIsLoadingInitial(false);
            isInitialLoadingRef.current = false;
        }
    });
    const reportLastRenderedItem = (range) => {
        if (onItemNewItemRender && range.endIndex > oldestMessagerReported) {
            const lastItem = store.getItem(range.endIndex - firstItemIndex);
            onItemNewItemRender(lastItem);
            setOldestMessageReported(range.endIndex);
        }
    };
    const handleFollowOutput = (atBottom) => {
        if (atBottom && !isScrolling.current) {
            return 'smooth';
        }
        return false;
    };
    useEffect(() => {
        if (chatId && !isInitialLoadingRef.current) {
            fetchInitialMessages();
        }
    }, [chatId]);
    useEffect(() => {
        store.append(incomingMessages);
        setMessages(store.messages);
        setTotalCount((prevTotalCount) => prevTotalCount + incomingMessages.length);
        if (!isAtBottom.current &&
            (shouldTriggerNewItemIndicator
                ? shouldTriggerNewItemIndicator(store.messages[store.messages.length - 1])
                : true)) {
            setHasNewMessages(true);
        }
    }, [incomingMessages]);
    useEffect(() => {
        store.updateMultiple(updatedMessages);
        setMessages([...store.messages]); // TODO: Spread operator is a hack to force a re-render
    }, [updatedMessages]);
    const handleLoadMore = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!isLoadingOlder && hasMore && !isLoadingInitial) {
            setIsLoadingOlder(true);
            const oldestMessageId = (_a = messages[0]) === null || _a === void 0 ? void 0 : _a.id;
            if (oldestMessageId) {
                const { messages: olderMessages, hasOlder: olderHasMore } = yield loadPrevMessages(messages[0].id);
                if (olderMessages.length > 0) {
                    setFirstItemIndex((prevFirstItemIndex) => prevFirstItemIndex - olderMessages.length);
                    store.prepend(olderMessages);
                    setMessages(store.messages);
                }
                setHasMore(olderHasMore);
            }
            else {
                setHasMore(false);
            }
            setIsLoadingOlder(false);
        }
    }), [loadPrevMessages, isLoadingOlder, hasMore, messages, isLoadingInitial]);
    const handleRenderItem = useCallback((index, item) => {
        const prevMessageIndex = messages.length - (totalCount - index + 1);
        if (prevMessageIndex < 0) {
            return render(item);
        }
        const prevMessage = messages[prevMessageIndex];
        return render(item, prevMessage);
    }, [messages, render, firstItemIndex]);
    const NewMessageIndicator = React.createElement(newMessageIndicator, {
        onClick: scrollToBottom,
    });
    return (_jsxs(VirtuosoWrapper, Object.assign({ style: Object.assign({ position: 'relative' }, style) }, { children: [isLoadingInitial && _jsx(OverlayDiv, { type: "loading" }), !isLoadingInitial && (messages === null || messages === void 0 ? void 0 : messages.length) === 0 && _jsx(NoMessages, {}), hasNewMessages && NewMessageIndicator, !isLoadingInitial && (_jsx(Virtuoso, { style: { height: '100%', width: '100%' }, itemContent: handleRenderItem, computeItemKey: (_, item) => store.computeKey(item), followOutput: handleFollowOutput, data: messages, initialTopMostItemIndex: {
                    index: 'LAST',
                    behavior: 'auto',
                }, startReached: handleLoadMore, endReached: () => {
                    setHasNewMessages(false);
                }, rangeChanged: reportLastRenderedItem, firstItemIndex: firstItemIndex, totalCount: totalCount, isScrolling: (scrolling) => {
                    isScrolling.current = scrolling;
                }, atBottomStateChange: (atBottom) => {
                    isAtBottom.current = atBottom;
                }, ref: listHandler, overscan: { reverse: 500, main: 0 }, increaseViewportBy: { top: 200, bottom: 200 }, totalListHeightChanged: (height) => {
                    // Force re-render when height changes
                    if (height > 0) {
                        setMessages([...store.messages]);
                    }
                } }))] })));
};
export default VirtualizedChat;
